import { useRef, useState, type PointerEvent } from "react";
import { useNavigate } from "react-router";
import { cn } from "../../lib/cn";
import { IconButton } from "../../components/ui/Button";
import { IconArrowRightUp, IconEyeOff, IconPip, IconPipExit } from "../../components/icons";
import { useStreamUi, useStreamVolume } from "./stores";
import { LiveBadge, StreamVideo, StreamVolume, pipSupported, usePictureInPicture } from "./StreamTile";
import { useVoice } from "./VoiceProvider";

type Corner = "tl" | "tr" | "bl" | "br";
const CORNER_KEY = "atrium:stream:corner";
// Top corners sit below page headers; bottom corners sit above the mobile tab bar
// and, on desktop, above message composers so the send button stays reachable.
const CORNERS: Record<Corner, string> = {
  tl: "left-4 top-16",
  tr: "right-4 top-16",
  bl: "left-4 bottom-[calc(env(safe-area-inset-bottom)+5rem)] md:bottom-24",
  br: "right-4 bottom-[calc(env(safe-area-inset-bottom)+5rem)] md:bottom-24",
};
const overlayButton = "text-white hover:bg-white/15 hover:text-white";

/**
 * A stream you're watching keeps playing in a small floating player when you
 * leave its room. Drag it to any corner; click it to go back.
 */
export function MiniPlayer() {
  const voice = useVoice();
  const navigate = useNavigate();
  const views = useStreamUi((s) => s.views);
  const focused = useStreamUi((s) => s.focused);
  const [corner, setCorner] = useState<Corner>(() => {
    const c = localStorage.getItem(CORNER_KEY);
    return c && c in CORNERS ? (c as Corner) : "br";
  });
  const [offset, setOffset] = useState<{ x: number; y: number } | null>(null);
  const drag = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const room = voice.room;
  const away = !!room && voice.status === "connected" && !(views[room.id] ?? 0);
  const candidates = away ? voice.participants.filter((p) => p.streaming && p.socketId !== voice.selfSocketId && voice.watching.has(p.socketId) && voice.screens[p.socketId]) : [];
  const p = candidates.find((c) => c.socketId === focused) ?? candidates[0] ?? null;
  const stream = p ? voice.screens[p.socketId] : undefined;
  const vol = useStreamVolume(p?.user.id ?? "");
  const [pip, togglePip] = usePictureInPicture(videoRef, stream);
  if (!room || !p || !stream) return null;

  const where = room.kind === "call" ? `/messages/${room.id}` : `/c/${room.communityId}/${room.id}`;
  const back = () => {
    useStreamUi.getState().setFocused(p.socketId);
    navigate(where);
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest("button,input")) return;
    drag.current = { x: e.clientX, y: e.clientY, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    const x = e.clientX - d.x;
    const y = e.clientY - d.y;
    if (!d.moved && Math.hypot(x, y) < 6) return;
    d.moved = true;
    setOffset({ x, y });
  };
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    setOffset(null);
    if (!d.moved) return back();
    // Snap to the corner nearest to where it was dropped.
    const next: Corner = `${e.clientY < window.innerHeight / 2 ? "t" : "b"}${e.clientX < window.innerWidth / 2 ? "l" : "r"}`;
    setCorner(next);
    localStorage.setItem(CORNER_KEY, next);
  };

  return (
    <div
      role="region"
      aria-label={`${p.user.displayName}'s stream`}
      className={cn(
        "group fixed z-40 aspect-video w-56 touch-none select-none overflow-hidden rounded-xl bg-black shadow-pop md:w-80",
        offset ? "cursor-grabbing" : "cursor-pointer",
        CORNERS[corner],
      )}
      style={offset ? { transform: `translate(${offset.x}px, ${offset.y}px)` } : undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => {
        drag.current = null;
        setOffset(null);
      }}
    >
      <StreamVideo stream={stream} muted={voice.deafened || vol.muted} volume={vol.volume} label={`${p.user.displayName}'s screen`} videoRef={videoRef} />
      <div className="pointer-events-none absolute left-2 top-2 flex max-w-[calc(100%-1rem)] items-center gap-1.5 rounded-md bg-overlay px-2 py-1 text-xs font-medium text-white">
        <LiveBadge />
        <span className="truncate">{p.user.displayName}</span>
      </div>
      <div className="absolute bottom-2 right-2 flex items-center gap-0.5 rounded-md bg-overlay p-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100">
        {stream.getAudioTracks().length > 0 && <StreamVolume p={p} />}
        {pipSupported() && <IconButton size="sm" label={pip ? "Exit picture in picture" : "Picture in picture"} icon={pip ? IconPipExit : IconPip} onClick={togglePip} className={overlayButton} />}
        <IconButton size="sm" label={`Back to ${room.name}`} icon={IconArrowRightUp} onClick={back} className={overlayButton} />
        <IconButton size="sm" label="Stop watching" icon={IconEyeOff} onClick={() => voice.setWatching(p.socketId, false)} className={overlayButton} />
      </div>
    </div>
  );
}
